import { localDateTransformer } from "@/utils/zodTransformers";
import { z } from "zod";

// Nested schema for CaseIntake relationship
export const caseIntakeSchemaInput = z.object({
    idUUID: z.string(),
    createdAt: z.coerce.date().optional(),
    updated: z.coerce.date().optional(),
    passengers: z.string().optional().nullable(),
    citationsTicketsIssued: z.string().optional().nullable(),
    citationsWhoReceived: z.string().optional().nullable(),
    treatment: z.string().optional().nullable(),
    emsUsed: z.string().optional().nullable(),
    emsCarrier: z.string().optional().nullable(),
    propertyDamage: z.string().optional().nullable(),
    intakeCompleted: z.boolean().optional().nullable(),
    incidentLocation: z.string().optional().nullable(),
    incidentReportDoneWho: z.string().optional().nullable(),
    injury: z.string().optional().nullable(),
    insAdditionalInfo: z.string().optional().nullable(),
    accidentSummary: z.string().optional().nullable(),
});

// Nested schema for CaseDisbursement relationship
export const caseDisbursementSchemaInput = z.object({
    idUUID: z.string(),
    createdAt: z.coerce.date().optional(),
    updated: z.coerce.date().optional(),
    negotiatedLiability: z.union([z.number(), z.string()]).optional().nullable(),
    negotiatedUIUIM: z.union([z.number(), z.string()]).optional().nullable(),
    attorneyFeePercentage: z.union([z.string(), z.number()]).optional().nullable(),
    attorneyFeeManualOverride: z.union([z.string(), z.number()]).optional().nullable(),
    clientDistributionManualOverride: z.union([z.string(), z.number()]).optional().nullable(),
    negotiatedLiabilityManualOverride: z.union([z.string(), z.number()]).optional().nullable(),
    negotiatedUiuimManualOverride: z.union([z.string(), z.number()]).optional().nullable(),
    costBill: z.union([z.string(), z.number()]).optional().nullable(),
    costInsurance: z.union([z.string(), z.number()]).optional().nullable(),
    costAdj: z.union([z.string(), z.number()]).optional().nullable(),
    costSelf: z.union([z.string(), z.number()]).optional().nullable(),
    costMedPay: z.union([z.string(), z.number()]).optional().nullable(),
    costCurrentBalance: z.union([z.string(), z.number()]).optional().nullable(),
    costReduction: z.union([z.string(), z.number()]).optional().nullable(),
    costSent: z.string().optional().nullable(),
    costApproved: z.string().optional().nullable(),
    disbursementAttorneyFeePercentage: z.union([z.string(), z.number()]).optional().nullable(),
    disbursementAttorneyFeeLocked: z.boolean().optional().nullable(),
    disbursementAttorneyFeeValue: z.union([z.string(), z.number()]).optional().nullable(),
    disbursementClientDistributionLocked: z.boolean().optional().nullable(),
    disbursementClientDistributionValue: z.union([z.string(), z.number()]).optional().nullable(),
    disbursementCostReduction: z.union([z.string(), z.number()]).optional().nullable(),
    disbursementsLocked: z.boolean().optional().nullable(),
    disbursementsLockedAt: z.string().optional().nullable(),
    disbursementsLockedBy: z.string().optional().nullable(),
    healthInsuranceBill: z.union([z.string(), z.number()]).optional().nullable(),
    healthInsuranceCompany: z.string().optional().nullable(),
    healthInsuranceOnBehalfOf: z.string().optional().nullable(),
    healthInsuranceAccountNumber: z.string().optional().nullable(),
    healthInsuranceReduction: z.union([z.string(), z.number()]).optional().nullable(),
    mpuasBill: z.union([z.string(), z.number()]).optional().nullable(),
});

export const clientSchema = z.object({
    idUUID: z.string(),
    companyName: z.string().nullish(),
    addr1: z.string().nullish(),
    addr2: z.string().nullish(),
    addrCity: z.string().nullish(),
    addrState: z.string().nullish(),
    addrZip: z.string().nullish(),
    caseKey: z.number().nullish(),
});

export const caseFormSchemaInput = z.object({
    idUUID: z.string(),
    clientName: z.string().optional().nullable(),
    caseName: z.string().optional().nullable(),
    caseType: z.string().optional().nullable(),

    anteLitemState: z.string().optional().nullable(),
    dOICounty: z.string().optional().nullable(),
    dateOfIncident: localDateTransformer,
    statuteOfLimitations: localDateTransformer,

    anteLitemYesNo: z.string().optional().nullable(),
    anteLitemGovernmentEntity: z.string().optional().nullable(),
    anteLitem: localDateTransformer,
    anteLitemEntity: z.string().optional().nullable(),

    idCaseStatus: z.string().optional().nullable(),
    caseStatus: z.string().optional().nullable(),
    liabilityAccepted: z.string().optional().nullable(),
    demandLiabilitySentOn: localDateTransformer,
    litigationStatus: z.string().optional().nullable(),

    idCaseStatusUM: z.string().optional().nullable(),
    caseStatusUM: z.string().optional().nullable(),
    uMUIMAccepted: z.string().optional().nullable(),
    demandUMSentOn: localDateTransformer,
    erisaUMSentOn: localDateTransformer,
    litigationSubStatus: z.string().optional().nullable(),

    assignedTeam: z.string().optional().nullable(),
    assignedToAttorney: z.string().optional().nullable(),
    assignedToAttorneyPretty: z.string().optional().nullable(),
    assignedToDisbursementSpecialist: z.string().optional().nullable(),
    assignedToIntakeSpecialist: z.string().optional().nullable(),

    assignedToParalegal: z.string().optional().nullable(),
    assignedToParalegalPretty: z.string().optional().nullable(),
    assignedToMedicalRecordsClerk: z.string().optional().nullable(),
    assignedToMedicalRecordsClerkPretty: z.string().optional().nullable(),
    attorneyPhoneNumber: z.string().optional().nullable(),
    cMPhoneNumber: z.string().optional().nullable(),
    cMEmailAddress: z.string().optional().nullable(),
    attorneyEmailAddress: z.string().optional().nullable(),

    financeLoan: z.string().optional().nullable(),
    attorneyFee: z.string().optional().nullable(),
    atFaultParty: z.string().optional().nullable(),
    crmStatus: z.string().optional().nullable(),

    clientPIN: z.string().optional().nullable(),

    // health insurance
    insidCompany: z.string().optional().nullable(),
    insMemberName: z.string().optional().nullable(),
    insMemberID: z.string().optional().nullable(),
    insPlanNumber: z.string().optional().nullable(),

    // accident info
    accidentReportDone: z.string().optional().nullable(),
    incidentReportDoneWho: z.string().optional().nullable(),
    accidentCaseNumber: z.string().optional().nullable(),
    pDSettled: z.string().optional().nullable(),
    pDSettledAmount: z.string().optional().nullable(),
    mainInjury1: z.string().optional().nullable(),
    mainInjury2: z.string().optional().nullable(),
    injury: z.string().optional().nullable(),
    clientPainScale: z.string().optional().nullable(),
    primaryStatute: z.string().optional().nullable(),
    accidentSummary: z.string().optional().nullable(),
    incidentLocation: z.string().optional().nullable(),

    idReferredBy: z.string().nullable().optional(),
    idReferredByView: z.string().nullable().optional(),
    idReferredByCompany: z.string().nullable().optional(),
    idReferredByCompanyView: z.string().nullable().optional(),

    insClaimNumber: z.string().nullable().optional(),
    uMUIMCompanyName: z.string().nullable().optional(),
    inspolicy: z.string().nullable().optional(),
    coverageType: z.string().nullable().optional(),
    insamountPerPerson: z.string().nullable().optional(),
    insFormLimits: z.string().nullable().optional(),
    insmedicalPmtAmount: z.string().nullable().optional(),
    insClaimNumber2: z.string().nullable().optional(),
    uMUIMCompanyName2: z.string().nullable().optional(),
    inspolicy2: z.string().nullable().optional(),
    coverageType2: z.string().nullable().optional(),
    insamountPerPerson2: z.string().nullable().optional(),
    insFormLimits2: z.string().nullable().optional(),
    insmedicalPmtAmount2: z.string().nullable().optional(),
    insClaimNumber3: z.string().nullable().optional(),
    uMUIMCompanyName3: z.string().nullable().optional(),
    inspolicy3: z.string().nullable().optional(),
    coverageType3: z.string().nullable().optional(),
    insamountPerPerson3: z.string().nullable().optional(),
    insFormLimits3: z.string().nullable().optional(),
    insmedicalPmtAmount3: z.string().nullable().optional(),
    metaCreatedByPretty: z.string().nullable().optional(),
    metaCreatedWhen: z.coerce.date().nullable(),
    policyLimits: z.boolean().optional().nullable(),
    policyLimitsTimestamp: z.string().optional().nullable(),
    medpayRequired: z.boolean().optional().nullable(),
    medpayAmount: z.string().optional().nullable(),
    medpayReceived: z.string().optional().nullable(),
    caseExpensesLocked: z.boolean().optional().nullable(),
    caseExpensesLockedAt: z.string().optional().nullable(),
    caseExpensesLockedBy: z.string().optional().nullable(),
    caseActive: z.boolean().nullish(),
    caseActiveChangedAt: z.string().nullish(),
    caseActiveChangedBy: z.string().nullish(),

    // intake flow fields
    passengers: z.string().optional().nullable(),
    citationsTicketsIssued: z.string().optional().nullable(),
    citationsWhoReceived: z.string().optional().nullable(),
    treatment: z.string().optional().nullable(),
    emsUsed: z.string().optional().nullable(),
    emsCarrier: z.string().optional().nullable(),
    propertyDamage: z.string().optional().nullable(),
    intakeCompleted: z.boolean().optional().nullable(),
    insClientsIns: z.string().optional().nullable(),
    idAtFault: z.string().optional().nullable(),
    insAdditionalInfo: z.string().optional().nullable(),

    // negotiated amounts
    negotiatedLiability: z.union([z.number(), z.string()]).optional().nullable(),
    negotiatedUIUIM: z.union([z.number(), z.string()]).optional().nullable(),

    // Attorney fee and client distribution overrides
    attorneyFeePercentage: z.union([z.string(), z.number()]).optional().nullable(),
    attorneyFeeManualOverride: z.union([z.string(), z.number()]).optional().nullable(),
    clientDistributionManualOverride: z.union([z.string(), z.number()]).optional().nullable(),
    negotiatedLiabilityManualOverride: z.union([z.string(), z.number()]).optional().nullable(),
    negotiatedUiuimManualOverride: z.union([z.string(), z.number()]).optional().nullable(),

    // COST row fields
    costBill: z.union([z.string(), z.number()]).optional().nullable(),
    costInsurance: z.union([z.string(), z.number()]).optional().nullable(),
    costAdj: z.union([z.string(), z.number()]).optional().nullable(),
    costSelf: z.union([z.string(), z.number()]).optional().nullable(),
    costMedPay: z.union([z.string(), z.number()]).optional().nullable(),
    costCurrentBalance: z.union([z.string(), z.number()]).optional().nullable(),
    costReduction: z.union([z.string(), z.number()]).optional().nullable(),
    costSent: z.string().optional().nullable(),
    costApproved: z.string().optional().nullable(),

    // Disbursement calculation fields
    disbursementAttorneyFeePercentage: z.union([z.string(), z.number()]).optional().nullable(),
    disbursementAttorneyFeeLocked: z.boolean().optional().nullable(),
    disbursementAttorneyFeeValue: z.union([z.string(), z.number()]).optional().nullable(),
    disbursementClientDistributionLocked: z.boolean().optional().nullable(),
    disbursementClientDistributionValue: z.union([z.string(), z.number()]).optional().nullable(),
    disbursementCostReduction: z.union([z.string(), z.number()]).optional().nullable(),
});
